import React, { useState } from 'react';
import { Check2, ChevronDown, ChevronUp, X } from 'react-bootstrap-icons';
import { SaleCreatePayload } from '../../api/aiAgent';
import styles from './SaleDraftCard.module.scss';

interface SaleDraftCardProps {
  payload: SaleCreatePayload;
  disabled?: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
}

const money = (value: number | null | undefined, currency = 'USD') => {
  const n = Number(value || 0);
  try {
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency || 'USD'}`;
  }
};

/** Catalog prices / draft totals are always in org referenceCurrency (usually USD). */
const MoneyStack: React.FC<{
  amount: number;
  referenceCurrency: string;
  localCurrency?: string | null;
  exchangeRate?: number | null;
  strong?: boolean;
}> = ({
  amount,
  referenceCurrency,
  localCurrency,
  exchangeRate,
  strong = false,
}) => {
  const rate = Number(exchangeRate);
  const showLocal =
    Boolean(localCurrency) &&
    localCurrency!.toUpperCase() !== referenceCurrency.toUpperCase() &&
    Number.isFinite(rate) &&
    rate > 0;

  const primary = money(amount, referenceCurrency);
  const secondary = showLocal
    ? money(amount * rate, localCurrency || undefined)
    : null;

  return (
    <span className={styles.moneyStack}>
      {strong ? <strong>{primary}</strong> : <span>{primary}</span>}
      {secondary ? (
        <span className={styles.moneySecondary}>{secondary}</span>
      ) : null}
    </span>
  );
};

const SaleDraftCard: React.FC<SaleDraftCardProps> = ({
  payload,
  disabled = false,
  onConfirm,
  onDismiss,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const paymentCurrency = (payload.paymentCurrency || '').trim().toUpperCase();
  // Product prices and MayorX totals live in referenceCurrency — never paymentCurrency.
  const referenceCurrency = (
    payload.referenceCurrency ||
    'USD'
  )
    .trim()
    .toUpperCase() || 'USD';
  const localCurrency = (payload.currency || '').trim().toUpperCase() || null;
  const exchangeRate =
    typeof payload.exchangeRate === 'number' ? payload.exchangeRate : null;

  const orderDiscountPercent = Math.max(
    0,
    Math.min(100, Number(payload.orderDiscountPercent || 0))
  );
  const orderDiscountFactor = 1 - orderDiscountPercent / 100;
  const lineNet = (line: SaleCreatePayload['products'][number]) => {
    const price = Number(line.price || 0);
    const qty = Number(line.quantity || 0);
    const discountPct = Math.max(0, Math.min(100, Number(line.discount || 0)));
    return price * qty * (1 - discountPct / 100) * orderDiscountFactor;
  };
  const subtotal =
    payload.subtotal ??
    payload.products.reduce((sum, line) => sum + lineNet(line), 0);
  const ivaAmount =
    payload.ivaAmount ??
    payload.products.reduce(
      (sum, line) => sum + lineNet(line) * (Number(line.taxRate || 0) / 100),
      0
    );
  const total = payload.total ?? subtotal + ivaAmount;

  const moneyProps = {
    referenceCurrency,
    localCurrency,
    exchangeRate,
  };

  return (
    <div className={styles.card} role="group" aria-label="Borrador de venta">
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <span className={styles.badge}>Borrador</span>
          <button
            type="button"
            className={styles.detailsToggle}
            aria-expanded={showDetails}
            onClick={() => setShowDetails(v => !v)}
          >
            {showDetails ? 'Ocultar detalles' : 'Ver detalles'}
            {showDetails ? (
              <ChevronUp size={12} aria-hidden />
            ) : (
              <ChevronDown size={12} aria-hidden />
            )}
          </button>
        </div>
        <h3 className={styles.title}>Resumen de la venta</h3>
      </div>

      <dl className={styles.meta}>
        <div>
          <dt>Cliente</dt>
          <dd>{payload.clientName}</dd>
        </div>
        <div>
          <dt>Pago</dt>
          <dd>
            {payload.paymentMethod}
            {paymentCurrency ? ` · ${paymentCurrency}` : ''}
          </dd>
        </div>
      </dl>

      {localCurrency &&
      localCurrency !== referenceCurrency &&
      exchangeRate != null &&
      exchangeRate > 0 ? (
        <p className={styles.fxMeta}>
          Precios en {referenceCurrency}
          {` · ${referenceCurrency}→${localCurrency} · ${exchangeRate}`}
          {payload.exchangeSourceName
            ? ` · ${payload.exchangeSourceName}`
            : ''}
        </p>
      ) : (
        <p className={styles.fxMeta}>Precios en {referenceCurrency}</p>
      )}

      <ul className={styles.lines}>
        {payload.products.map((line, idx) => {
          const net = lineNet(line);
          return (
            <li key={`${line.productId}-${idx}`}>
              <div className={styles.lineMain}>
                <span className={styles.lineName}>
                  {line.name || line.code || line.productId}
                </span>
                <span className={styles.lineQty}>
                  {line.quantity} ×{' '}
                  <MoneyStack amount={Number(line.price || 0)} {...moneyProps} />
                </span>
              </div>
              {(line.code || line.productId) && (
                <span className={styles.lineCode}>{line.code || line.productId}</span>
              )}
              {showDetails && (
                <dl className={styles.lineDetails}>
                  {line.code ? (
                    <div>
                      <dt>Código</dt>
                      <dd>{line.code}</dd>
                    </div>
                  ) : null}
                  {line.discount ? (
                    <div>
                      <dt>Descuento</dt>
                      <dd>{line.discount}%</dd>
                    </div>
                  ) : null}
                  {line.taxRate != null && Number(line.taxRate) > 0 ? (
                    <div>
                      <dt>Impuesto</dt>
                      <dd>
                        {line.taxName ? `${line.taxName} · ` : ''}
                        {line.taxRate}%
                      </dd>
                    </div>
                  ) : null}
                  {line.warehouseId ? (
                    <div>
                      <dt>Almacén</dt>
                      <dd>{line.warehouseId}</dd>
                    </div>
                  ) : null}
                  <div>
                    <dt>Subtotal</dt>
                    <dd>
                      <MoneyStack amount={net} {...moneyProps} />
                    </dd>
                  </div>
                </dl>
              )}
            </li>
          );
        })}
      </ul>

      {showDetails && (
        <dl className={styles.extraDetails}>
          <div>
            <dt>Cliente</dt>
            <dd>{payload.clientName}</dd>
          </div>
          {payload.creatorSalesPerson ? (
            <div>
              <dt>Vendedor</dt>
              <dd>{payload.creatorSalesPerson}</dd>
            </div>
          ) : null}
          {payload.deliveryAddress ? (
            <div>
              <dt>Dirección de entrega</dt>
              <dd>{payload.deliveryAddress}</dd>
            </div>
          ) : null}
          {payload.comment ? (
            <div>
              <dt>Comentario</dt>
              <dd>{payload.comment}</dd>
            </div>
          ) : null}
          <div>
            <dt>Moneda precios</dt>
            <dd>{referenceCurrency}</dd>
          </div>
          {paymentCurrency ? (
            <div>
              <dt>Moneda de pago</dt>
              <dd>{paymentCurrency}</dd>
            </div>
          ) : null}
          {typeof payload.exchangeRate === 'number' ? (
            <div>
              <dt>Tasa de cambio</dt>
              <dd>
                {payload.exchangeRate}
                {payload.referenceCurrency && payload.currency
                  ? ` (${payload.referenceCurrency}→${payload.currency})`
                  : ''}
                {payload.exchangeSourceName
                  ? ` · ${payload.exchangeSourceName}`
                  : ''}
              </dd>
            </div>
          ) : null}
          {orderDiscountPercent > 0 ? (
            <div>
              <dt>Descuento general</dt>
              <dd>{orderDiscountPercent}%</dd>
            </div>
          ) : null}
        </dl>
      )}

      <div className={styles.totalsBlock}>
        <div className={styles.totalsRow}>
          <span>Subtotal</span>
          <MoneyStack amount={Number(subtotal)} {...moneyProps} />
        </div>
        {orderDiscountPercent > 0 ? (
          <div className={styles.totalsRow}>
            <span>Descuento general</span>
            <span>{orderDiscountPercent}%</span>
          </div>
        ) : null}
        {ivaAmount > 0 ? (
          <div className={styles.totalsRow}>
            <span>IVA</span>
            <MoneyStack amount={Number(ivaAmount)} {...moneyProps} />
          </div>
        ) : null}
        <div className={styles.totals}>
          <span>Total</span>
          <MoneyStack amount={Number(total)} {...moneyProps} strong />
        </div>
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.dismiss}
          disabled={disabled}
          onClick={onDismiss}
        >
          <X size={16} aria-hidden />
          Cancelar
        </button>
        <button
          type="button"
          className={styles.confirm}
          disabled={disabled}
          onClick={onConfirm}
        >
          <Check2 size={16} aria-hidden />
          Confirmar venta
        </button>
      </div>
    </div>
  );
};

export default SaleDraftCard;
